// Workspace info panel — identity, ingest timestamps, sign-out,
// and a danger-zone delete.
//
// Phase 6.3.2 (A.5 + B.2): migrated to the <SidePanel> primitive,
// opening from the RIGHT (was an inline LEFT column — inconsistent
// with the thread pane). SidePanel owns the chrome (slide-in, Esc,
// focus trap, close X, focus return). The delete confirmation is a
// <Modal> that stacks on top; the shared overlay stack means Esc
// closes the dialog first, then the panel.

import { useRef, useState } from "react";
import { LogOut, Trash2 } from "lucide-react";

import { type WorkspaceSummary, deleteWorkspace } from "../lib/api";
import { useAuth } from "../state/AuthContext";
import { DeleteWorkspaceDialog } from "./DeleteWorkspaceDialog";
import { SidePanel } from "./ui/SidePanel";

interface WorkspaceInfoPanelProps {
  teamID: string;
  summary: WorkspaceSummary;
  onClose: () => void;
  onLogout?: () => void;
  conversationCount?: number | null;
  onDeleted?: () => void;
}

export function WorkspaceInfoPanel({
  teamID,
  summary,
  onClose,
  onLogout,
  conversationCount = null,
  onDeleted,
}: WorkspaceInfoPanelProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  // Phase 8.2: deletion is superadmin-only (users add, admins delete) —
  // hide the danger zone entirely for regular users; the backend 403s
  // them regardless.
  const { role } = useAuth();
  const canDelete = role === "superadmin";

  return (
    <SidePanel open onClose={onClose} title={summary.team_name} side="right" size="md">
      <div data-testid={`workspace-info-panel-${teamID}`} className="space-y-section">
        <div className="text-mono text-theme-text-muted">team_id: {teamID}</div>

        {onLogout && (
          <section className="border-t border-theme-border-subtle pt-loose">
            <button
              type="button"
              onClick={onLogout}
              data-testid={`workspace-info-logout-${teamID}`}
              className="inline-flex items-center gap-tight px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-secondary hover:text-theme-error-text hover:border-theme-error-border transition-colors duration-150"
            >
              <LogOut className="w-3 h-3" strokeWidth={1.5} aria-hidden />
              Sign out
            </button>
          </section>
        )}

        {canDelete && (
          <section className="border-t border-theme-error-border pt-loose">
            <h3
              data-testid={`workspace-info-danger-zone-${teamID}`}
              className="text-caption text-theme-error-text mb-snug"
            >
              Danger zone
            </h3>
            <button
              ref={deleteTriggerRef}
              type="button"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid={`workspace-info-delete-trigger-${teamID}`}
              className="inline-flex items-center gap-tight px-3 py-1.5 border border-theme-error-border text-caption text-theme-error-text hover:bg-theme-error-bg transition-colors duration-150"
            >
              <Trash2 className="w-3 h-3" strokeWidth={1.5} aria-hidden />
              Delete workspace
            </button>
          </section>
        )}
      </div>

      {canDelete && deleteDialogOpen && (
        <DeleteWorkspaceDialog
          workspaceName={summary.team_name}
          conversationCount={conversationCount}
          onClose={() => {
            setDeleteDialogOpen(false);
            window.setTimeout(() => deleteTriggerRef.current?.focus(), 0);
          }}
          onConfirm={async () => {
            await deleteWorkspace(teamID);
            setDeleteDialogOpen(false);
            onDeleted?.();
          }}
        />
      )}
    </SidePanel>
  );
}
